/**
 * 留资管理页 — Leads（批 12 重写：后台销售线索管理）。
 *
 * GET   /api/admin/leads（admin）：分页 + 跟进状态 Tab 筛选 + 手机号/品牌名模糊搜索；
 * PATCH /api/admin/leads/{id}     ：跟进状态下拉 / 备注编辑（写 followed_at）；
 * 查看报告：诊断任务 done 时打开 /api/public/diag/report/{task_id}?token= 链接。
 * 页面入口仅 admin 可见（侧边栏 adminOnly + 后端 4030 双保险）。
 */
import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Link,
  MenuItem,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchOutlined from "@mui/icons-material/SearchOutlined";
import EditOutlined from "@mui/icons-material/EditOutlined";
import OpenInNewOutlined from "@mui/icons-material/OpenInNewOutlined";
import PageHeader from "../components/PageHeader";
import Badge from "../components/Badge";
import Loading from "../components/Loading";
import EmptyState from "../components/EmptyState";
import {
  DIAG_STATUS_META,
  FOLLOW_STATUS_META,
  getAdminLeads,
  updateLeadFollow,
  type AdminLeadItem,
  type FollowStatus,
} from "../api/leads";
import { formatDateTime } from "../utils/format";

const FOLLOW_TABS = [
  { value: "", label: "全部" },
  { value: "uncontacted", label: "未联系" },
  { value: "contacted", label: "已联系" },
  { value: "converted", label: "已转化" },
  { value: "invalid", label: "无效" },
];

/** 编辑备注/跟进状态对话框。 */
function EditFollowDialog({
  lead,
  onClose,
  onSaved,
}: {
  lead: AdminLeadItem | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [status, setStatus] = useState<FollowStatus>("uncontacted");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (lead) {
      setStatus(lead.follow_status);
      setNote(lead.follow_note ?? "");
      setErr(null);
    }
  }, [lead]);

  const save = async () => {
    if (!lead) return;
    setSaving(true);
    setErr(null);
    try {
      await updateLeadFollow(lead.id, { follow_status: status, follow_note: note });
      onSaved();
      onClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "保存失败");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!lead} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>编辑跟进 — {lead?.company_name}</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
        {err && <Alert severity="error">{err}</Alert>}
        <TextField
          select
          size="small"
          label="跟进状态"
          value={status}
          onChange={(e) => setStatus(e.target.value as FollowStatus)}
        >
          {Object.entries(FOLLOW_STATUS_META).map(([v, m]) => (
            <MenuItem key={v} value={v}>
              {m.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          label="跟进备注"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          multiline
          minRows={3}
          inputProps={{ maxLength: 500 }}
          helperText={`${note.length}/500`}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>取消</Button>
        <Button variant="contained" onClick={save} disabled={saving}>
          {saving ? "保存中…" : "保存"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function Leads() {
  const [items, setItems] = useState<AdminLeadItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0); // MUI 0 基
  const [size, setSize] = useState(20);
  const [tab, setTab] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<AdminLeadItem | null>(null);
  const [inlineErr, setInlineErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getAdminLeads({
        page: page + 1,
        size,
        follow_status: tab || undefined,
        search: search || undefined,
      });
      setItems(data.items);
      setTotal(data.total);
    } catch {
      setError("线索列表加载失败（需管理员权限）");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [page, size, tab, search]);

  useEffect(() => {
    void load();
  }, [load]);

  /** 行内跟进状态下拉快速流转。 */
  const changeStatus = async (lead: AdminLeadItem, next: FollowStatus) => {
    setInlineErr(null);
    try {
      await updateLeadFollow(lead.id, { follow_status: next });
      setItems((prev) =>
        prev.map((it) => (it.id === lead.id ? { ...it, follow_status: next } : it)),
      );
    } catch (e) {
      setInlineErr(e instanceof Error ? e.message : "状态更新失败");
    }
  };

  return (
    <Box>
      <PageHeader
        title="留资管理"
        subtitle="官网免费诊断与演示站留资线索，跟进状态流转与报告查看（仅运营方可见）"
      />
      <Card>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              mb: 2,
              alignItems: "center",
              flexWrap: "wrap",
              justifyContent: "space-between",
            }}
          >
            <Tabs
              value={tab}
              onChange={(_e, v) => {
                setTab(v);
                setPage(0);
              }}
            >
              {FOLLOW_TABS.map((t) => (
                <Tab key={t.value} value={t.value} label={t.label} />
              ))}
            </Tabs>
            <TextField
              size="small"
              placeholder="手机号 / 品牌名搜索"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setSearch(searchInput.trim());
                  setPage(0);
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchOutlined fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <Button
                      size="small"
                      onClick={() => {
                        setSearch(searchInput.trim());
                        setPage(0);
                      }}
                    >
                      搜索
                    </Button>
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 280 }}
            />
          </Box>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {inlineErr && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setInlineErr(null)}>
              {inlineErr}
            </Alert>
          )}
          {loading ? (
            <Loading />
          ) : items.length === 0 ? (
            <EmptyState text="暂无线索" subtitle="当前筛选条件下没有线索记录" />
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>手机号</TableCell>
                  <TableCell>品牌 / 公司</TableCell>
                  <TableCell>官网</TableCell>
                  <TableCell>诊断状态</TableCell>
                  <TableCell>提交时间</TableCell>
                  <TableCell>跟进状态</TableCell>
                  <TableCell>备注</TableCell>
                  <TableCell align="right">操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((lead) => {
                  const task = lead.latest_task;
                  const diag = task ? DIAG_STATUS_META[task.status] : null;
                  return (
                    <TableRow key={lead.id} hover>
                      <TableCell>{lead.contact}</TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 180 }}>
                          {task?.brand_name || lead.company_name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {task?.website_url ? (
                          <Link
                            href={task.website_url}
                            target="_blank"
                            rel="noreferrer"
                            variant="body2"
                            noWrap
                            sx={{ maxWidth: 180, display: "inline-block" }}
                          >
                            {task.website_url}
                          </Link>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell>
                        {diag && task ? (
                          <Tooltip
                            title={
                              task.completed_at
                                ? `完成于 ${formatDateTime(task.completed_at)}`
                                : task.status === "failed"
                                  ? "诊断失败"
                                  : "报告生成中"
                            }
                          >
                            <span>
                              <Badge label={diag.label} color={diag.color} bgColor={diag.bg} />
                            </span>
                          </Tooltip>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell>
                        {lead.created_at ? formatDateTime(lead.created_at) : "—"}
                      </TableCell>
                      <TableCell>
                        <Select
                          size="small"
                          variant="standard"
                          disableUnderline
                          value={lead.follow_status}
                          onChange={(e) =>
                            void changeStatus(lead, e.target.value as FollowStatus)
                          }
                          renderValue={(v) => {
                            const m = FOLLOW_STATUS_META[v] ?? FOLLOW_STATUS_META.uncontacted;
                            return <Badge label={m.label} color={m.color} bgColor={m.bg} />;
                          }}
                        >
                          {Object.entries(FOLLOW_STATUS_META).map(([v, m]) => (
                            <MenuItem key={v} value={v}>
                              {m.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                          {lead.follow_note || "—"}
                        </Typography>
                        {lead.followed_at && (
                          <Typography variant="caption" color="text.secondary">
                            {formatDateTime(lead.followed_at)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {task?.report_url && (
                          <Tooltip title="查看报告">
                            <IconButton
                              size="small"
                              onClick={() => window.open(task.report_url!, "_blank")}
                            >
                              <OpenInNewOutlined fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="编辑备注">
                          <IconButton size="small" onClick={() => setEditing(lead)}>
                            <EditOutlined fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_e, p) => setPage(p)}
            rowsPerPage={size}
            onRowsPerPageChange={(e) => {
              setSize(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 20, 50]}
            labelRowsPerPage="每页"
          />
        </CardContent>
      </Card>
      <EditFollowDialog
        lead={editing}
        onClose={() => setEditing(null)}
        onSaved={() => void load()}
      />
    </Box>
  );
}
